/** Ink colors offered by the pen tools (Jamboard's six first). */
export const PEN_COLORS = [
  '#202124',
  '#1a73e8',
  '#188038',
  '#f9ab00',
  '#d93025',
  '#ffffff',
  '#9334e6',
  '#e8710a',
  '#12b5cb',
  '#e52592',
] as const;

export const HIGHLIGHTER_COLORS = [
  '#ffeb3b',
  '#a6f68f',
  '#80deea',
  '#ffab91',
  '#f48fb1',
  '#b39ddb',
] as const;

export const STICKY_COLORS = [
  { name: 'Yellow', value: '#fff176' },
  { name: 'Orange', value: '#ffcc80' },
  { name: 'Pink', value: '#f8bbd0' },
  { name: 'Purple', value: '#d1c4e9' },
  { name: 'Blue', value: '#b3e5fc' },
  { name: 'Green', value: '#c5e1a5' },
  { name: 'Teal', value: '#b2dfdb' },
  { name: 'Gray', value: '#e0e0e0' },
] as const;

/** Fill / stroke swatches for shapes, connectors and text. */
export const SHAPE_COLORS = [
  'transparent',
  '#ffffff',
  '#202124',
  '#5f6368',
  '#d93025',
  '#e8710a',
  '#f9ab00',
  '#188038',
  '#1a73e8',
  '#9334e6',
  '#e52592',
  '#fce8e6',
  '#fef7e0',
  '#e6f4ea',
  '#e8f0fe',
  '#f3e8fd',
] as const;

/** Presence colors: distinct, saturated and readable with white text. */
export const USER_COLORS = [
  '#e8453c',
  '#f29900',
  '#1e8e3e',
  '#1a73e8',
  '#a142f4',
  '#e52592',
  '#12a4af',
  '#d56e0c',
  '#5c6bc0',
  '#7cb342',
  '#8d6e63',
  '#00897b',
] as const;

/** Stable color for a user or guest id. */
export function colorForId(id: string): string {
  let hash = 5381;
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) + hash + id.charCodeAt(i)) | 0;
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length] as string;
}

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) return null;
  let h = m[1];
  if (h.length === 3) h = [...h].map((c) => c + c).join('');
  const n = Number.parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Relative luminance (0 = black, 1 = white) per WCAG. */
export function luminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 1;
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function isDarkColor(hex: string): boolean {
  return luminance(hex) < 0.35;
}

/** Black or white, whichever reads better on `background`. */
export function contrastingTextColor(background: string): string {
  return isDarkColor(background) ? '#ffffff' : '#202124';
}

function toHex(rgb: readonly number[]): string {
  return `#${rgb
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** `a` blended towards `b`; `amount` 0 is all `a`, 1 is all `b`. */
export function mixColors(a: string, b: string, amount: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return a;
  return toHex(ca.map((v, i) => v + ((cb[i] as number) - v) * amount));
}

/** The dark gray that light paper becomes in dark mode. */
const DARK_PAPER = '#1b1c1f';

/**
 * The frame's paper as one person sees it. The paper (and its pattern) is
 * shared, but light paper is drawn dark for people who use dark mode, keeping
 * a hint of its tint. Paper that is already dark looks the same to everyone.
 */
export function viewPaper(color: string, darkMode: boolean): string {
  if (!darkMode || isDarkColor(color)) return color;
  return mixColors(color, DARK_PAPER, 0.92);
}

/**
 * Ink (strokes, text, lines) as one person sees it. On paper that is drawn
 * dark for them only, dark ink would disappear, so its lightness is flipped:
 * black reads as near-white and dark gray as light gray, while brighter
 * colors stay as they are. Everyone else sees the stored color.
 */
export function viewInk(color: string, paperDarkened: boolean): string {
  if (!paperDarkened) return color;
  const rgb = parseHex(color);
  if (!rgb) return color;
  const [r, g, b] = rgb.map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  if (lightness >= 0.4) return color;
  // Same hue and saturation, mirrored lightness: shift every channel equally.
  const shift = (1 - 2 * lightness) * 255;
  return toHex(rgb.map((v) => v + shift));
}
